// admin-panel/src/services/api.js
import axios from 'axios';
import { toast } from 'react-hot-toast';

// API Configuration
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const API_TIMEOUT = 30000; // 30 seconds

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: API_TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
});

// Request interceptor - Add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - Handle errors globally
api.interceptors.response.use(
  (response) => {
    return response.data;
  },
  (error) => {
    // Handle errors globally
    if (error.response) {
      const { status, data } = error.response;
      
      switch (status) {
        case 401:
          // Unauthorized - Token expired or invalid
          localStorage.removeItem('authToken');
          localStorage.removeItem('userData');
          window.location.href = '/login';
          toast.error('Session expired. Please login again.');
          break;
        
        case 403:
          // Forbidden - Insufficient permissions
          toast.error('You do not have permission to perform this action.');
          break;
        
        case 404:
          // Not found
          toast.error('Resource not found.');
          break;
        
        case 422:
          // Validation error
          if (data.errors) {
            Object.values(data.errors).forEach((err) => {
              toast.error(err[0]);
            });
          } else {
            toast.error(data.message || 'Validation failed.');
          }
          break;
        
        case 500:
          // Server error
          toast.error('Server error. Please try again later.');
          break;
        
        default:
          toast.error(data?.message || 'An error occurred.');
      }
    } else if (error.request) {
      // Network error
      toast.error('Network error. Please check your connection.');
    } else {
      // Other errors
      toast.error('An unexpected error occurred.');
    }
    
    return Promise.reject(error);
  }
);

// Authentication API
export const authAPI = {
  // Login
  login: (credentials) => 
    api.post('/auth/login', credentials),
  
  // Register
  register: (userData) => 
    api.post('/auth/register', userData),
  
  // Get current user
  getCurrentUser: () => 
    api.get('/auth/me'),
  
  // Refresh token
  refreshToken: () => 
    api.post('/auth/refresh'),
  
  // Logout
  logout: () => 
    api.post('/auth/logout'),
  
  // Change password
  changePassword: (data) => 
    api.post('/auth/change-password', data),
};

// Admin API
export const adminAPI = {
  // Users Management
  getUsers: (params = {}) => 
    api.get('/admin/users', { params }),
  
  getUserById: (id) => 
    api.get(`/admin/users/${id}`),
  
  createUser: (userData) => 
    api.post('/admin/users', userData),
  
  updateUser: (id, userData) => 
    api.put(`/admin/users/${id}`, userData),
  
  deleteUser: (id) => 
    api.delete(`/admin/users/${id}`),
  
  bulkCreateUsers: (usersData) => 
    api.post('/admin/users/bulk', usersData),
  
  // Subjects Management
  getSubjects: (params = {}) => 
    api.get('/admin/subjects', { params }),
  
  getSubjectById: (id) => 
    api.get(`/admin/subjects/${id}`),
  
  createSubject: (subjectData) => 
    api.post('/admin/subjects', subjectData),
  
  updateSubject: (id, subjectData) => 
    api.put(`/admin/subjects/${id}`, subjectData),
  
  deleteSubject: (id) => 
    api.delete(`/admin/subjects/${id}`),
  
  // Face Registration
  registerFace: (userId, faceData) => 
    api.post(`/admin/face-register/${userId}`, faceData),
  
  bulkFaceRegister: (data) => 
    api.post('/admin/face-register/bulk', data),
  
  // Time Settings
  getTimeSettings: () => 
    api.get('/admin/settings/time'),
  
  updateTimeSettings: (settings) => 
    api.put('/admin/settings/time', settings),
  
  // System Settings
  getSystemSettings: () => 
    api.get('/admin/settings/system'),
  
  updateSystemSettings: (settings) => 
    api.put('/admin/settings/system', settings),
};

// Faculty API
export const facultyAPI = {
  // Faculty subjects
  getFacultySubjects: () => 
    api.get('/faculty/subjects'),
  
  getSubjectAttendance: (subjectId, date) => 
    api.get(`/faculty/attendance/subject/${subjectId}`, { params: { date } }),
  
  markBulkAttendance: (data) => 
    api.post('/faculty/attendance/bulk', data),
  
  updateAttendance: (attendanceId, data) => 
    api.put(`/faculty/attendance/${attendanceId}`, data),
  
  // Class reports
  getClassReport: (subjectId, params = {}) => 
    api.get(`/faculty/reports/class/${subjectId}`, { params }),
  
  exportClassReport: (subjectId, format = 'csv') => 
    api.get(`/faculty/reports/class/${subjectId}/export`, { 
      params: { format },
      responseType: 'blob'
    }),
  
  // Faculty profile
  getFacultyProfile: () => 
    api.get('/faculty/profile'),
  
  updateFacultyProfile: (data) => 
    api.put('/faculty/profile', data),
};

// Student API
export const studentAPI = {
  // Student subjects
  getTodaySubjects: () => 
    api.get('/student/subjects/today'),
  
  getAllSubjects: () => 
    api.get('/student/subjects'),
  
  // Attendance
  markAttendance: (data) => 
    api.post('/student/attendance/mark', data),
  
  getAttendanceHistory: (params = {}) => 
    api.get('/student/attendance/history', { params }),
  
  getAttendanceStats: () => 
    api.get('/student/attendance/stats'),
  
  // Student profile
  getStudentProfile: () => 
    api.get('/student/profile'),
  
  updateStudentProfile: (data) => 
    api.put('/student/profile', data),
};

// Parent API
export const parentAPI = {
  // Children management
  getChildren: () => 
    api.get('/parent/children'),
  
  getChildDetails: (childId) => 
    api.get(`/parent/children/${childId}`),
  
  // Child attendance
  getChildAttendance: (childId, params = {}) => 
    api.get(`/parent/attendance/${childId}`, { params }),
  
  getChildAttendanceStats: (childId) => 
    api.get(`/parent/attendance/${childId}/stats`),
  
  // Notifications
  getNotifications: (params = {}) => 
    api.get('/parent/notifications', { params }),
  
  markNotificationRead: (notificationId) => 
    api.put(`/parent/notifications/${notificationId}/read`),
  
  markAllNotificationsRead: () => 
    api.put('/parent/notifications/read-all'),
  
  deleteNotification: (notificationId) => 
    api.delete(`/parent/notifications/${notificationId}`),
  
  // Parent profile
  getParentProfile: () => 
    api.get('/parent/profile'),
  
  updateParentProfile: (data) => 
    api.put('/parent/profile', data),
};

// Reports API
export const reportsAPI = {
  // Daily reports
  getDailyReport: (date) => 
    api.get('/reports/daily', { params: { date } }),
  
  exportDailyReport: (date, format = 'csv') => 
    api.get('/reports/daily/export', { 
      params: { date, format },
      responseType: 'blob'
    }),
  
  // Monthly reports
  getMonthlyReport: (month, year) => 
    api.get('/reports/monthly', { params: { month, year } }),
  
  exportMonthlyReport: (month, year, format = 'csv') => 
    api.get('/reports/monthly/export', { 
      params: { month, year, format },
      responseType: 'blob'
    }),
  
  // Custom reports
  getCustomReport: (params) => 
    api.get('/reports/custom', { params }),
  
  exportCustomReport: (params) => 
    api.get('/reports/custom/export', { 
      params,
      responseType: 'blob'
    }),
  
  // Analytics
  getAttendanceAnalytics: (params = {}) => 
    api.get('/reports/analytics/attendance', { params }),
  
  getStudentAnalytics: () => 
    api.get('/reports/analytics/students'),
  
  getFacultyAnalytics: () => 
    api.get('/reports/analytics/faculty'),
  
  // System logs
  getSystemLogs: (params = {}) => 
    api.get('/reports/logs', { params }),
};

// Face Recognition API
export const faceAPI = {
  // Verify face
  verifyFace: (faceData) => 
    api.post('/face/verify', faceData),
  
  // Register face (for admin)
  registerFace: (userId, faceData) => 
    api.post(`/face/register/${userId}`, faceData),
  
  // Update face embedding
  updateFaceEmbedding: (userId, embedding) => 
    api.put(`/face/embedding/${userId}`, { embedding }),
  
  // Delete face data
  deleteFaceData: (userId) => 
    api.delete(`/face/data/${userId}`),
  
  // Face recognition settings
  getFaceSettings: () => 
    api.get('/face/settings'),
  
  updateFaceSettings: (settings) => 
    api.put('/face/settings', settings),
};

// Notifications API
export const notificationsAPI = {
  // Get all notifications
  getNotifications: (params = {}) => 
    api.get('/notifications', { params }),
  
  // Get unread count
  getUnreadCount: () => 
    api.get('/notifications/unread-count'),
  
  // Mark as read
  markAsRead: (notificationId) => 
    api.put(`/notifications/${notificationId}/read`),
  
  // Mark all as read
  markAllAsRead: () => 
    api.put('/notifications/read-all'),
  
  // Delete notification
  deleteNotification: (notificationId) => 
    api.delete(`/notifications/${notificationId}`),
  
  // Send notification
  sendNotification: (data) => 
    api.post('/notifications/send', data),
  
  // SMS notifications
  sendSMS: (data) => 
    api.post('/notifications/sms', data),
  
  // Email notifications
  sendEmail: (data) => 
    api.post('/notifications/email', data),
};

// Dashboard API
export const dashboardAPI = {
  // Admin dashboard stats
  getAdminStats: () => 
    api.get('/dashboard/admin/stats'),
  
  // Faculty dashboard stats
  getFacultyStats: () => 
    api.get('/dashboard/faculty/stats'),
  
  // Student dashboard stats
  getStudentStats: () => 
    api.get('/dashboard/student/stats'),
  
  // Parent dashboard stats
  getParentStats: () => 
    api.get('/dashboard/parent/stats'),
  
  // Recent activities
  getRecentActivities: () => 
    api.get('/dashboard/recent-activities'),
  
  // Attendance summary
  getAttendanceSummary: (params = {}) => 
    api.get('/dashboard/attendance-summary', { params }),
  
  // Upcoming classes
  getUpcomingClasses: () => 
    api.get('/dashboard/upcoming-classes'),
  
  // Pending tasks
  getPendingTasks: () => 
    api.get('/dashboard/pending-tasks'),
};

// Export Management API
export const exportAPI = {
  // Export data
  exportData: (type, params = {}) => 
    api.get(`/export/${type}`, { 
      params,
      responseType: 'blob'
    }),
  
  // Get export history
  getExportHistory: () => 
    api.get('/export/history'),
  
  // Download export
  downloadExport: (exportId) => 
    api.get(`/export/download/${exportId}`, {
      responseType: 'blob'
    }),
  
  // Delete export
  deleteExport: (exportId) => 
    api.delete(`/export/${exportId}`),
};

// File Upload API
export const uploadAPI = {
  // Upload single file
  uploadFile: (file, config = {}) => {
    const formData = new FormData();
    formData.append('file', file);
    
    return api.post('/upload/file', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      ...config,
    });
  },
  
  // Upload multiple files
  uploadMultipleFiles: (files, config = {}) => {
    const formData = new FormData();
    files.forEach((file, index) => {
      formData.append(`files[${index}]`, file);
    });
    
    return api.post('/upload/files', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      ...config,
    });
  },
  
  // Upload student photos for bulk registration
  uploadStudentPhotos: (files, config = {}) => {
    const formData = new FormData();
    Object.keys(files).forEach((key) => {
      formData.append(`photos[${key}]`, files[key]);
    });
    
    return api.post('/upload/student-photos', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      ...config,
    });
  },
};

// Utility functions
export const apiUtils = {
  // Download file from blob
  downloadBlob: (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },
  
  // Format API error
  formatError: (error) => {
    if (error.response) {
      return error.response.data?.message || 'Server error occurred';
    } else if (error.request) {
      return 'Network error. Please check your connection.';
    } else {
      return error.message || 'An unexpected error occurred.';
    }
  },
  
  // Check if user has specific role
  hasRole: (requiredRole) => {
    const userData = JSON.parse(localStorage.getItem('userData') || '{}');
    return userData.role === requiredRole;
  },
  
  // Check if user has any of the required roles
  hasAnyRole: (requiredRoles) => {
    const userData = JSON.parse(localStorage.getItem('userData') || '{}');
    return requiredRoles.includes(userData.role);
  },
  
  // Get current user data
  getCurrentUser: () => {
    return JSON.parse(localStorage.getItem('userData') || '{}');
  },
  
  // Get auth token
  getAuthToken: () => {
    return localStorage.getItem('authToken');
  },
  
  // Set auth token and user data
  setAuthData: (token, userData) => {
    localStorage.setItem('authToken', token);
    localStorage.setItem('userData', JSON.stringify(userData));
  },
  
  // Clear auth data
  clearAuthData: () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('userData');
  },
  
  // Check if user is authenticated
  isAuthenticated: () => {
    return !!localStorage.getItem('authToken');
  },
};

// Export default api instance for custom requests
export default api;